import type {
  CloudCostInvoice,
  CloudCostLine,
  CloudCostReport,
} from "@hasheeai/types";
import { categoryTotals, invoiceGroups, pricedTotal } from "./cloud-cost-model";
type InvoiceItem = CloudCostInvoice["items"][number];
export function monthBounds(month: string): { start: number; end: number } {
  const start = Date.parse(`${month}-01T00:00:00Z`);
  const end = new Date(start);
  end.setUTCMonth(end.getUTCMonth() + 1);
  return { start, end: end.getTime() };
}
export function overlapsServiceMonth(
  item: InvoiceItem,
  month: string,
): boolean {
  const bounds = monthBounds(month);
  return (
    !!item.service_start &&
    !!item.service_end &&
    Date.parse(item.service_start) < bounds.end &&
    Date.parse(item.service_end) > bounds.start
  );
}
export function withinServiceMonth(item: InvoiceItem, month: string): boolean {
  const bounds = monthBounds(month);
  return (
    !!item.service_start &&
    !!item.service_end &&
    Date.parse(item.service_start) >= bounds.start &&
    Date.parse(item.service_end) <= bounds.end
  );
}
/** A full calendar-month invoice replaces metering, never adds to it. */
function settledInvoice(
  report: CloudCostReport,
  provider: string,
): CloudCostInvoice | undefined {
  const bounds = monthBounds(report.month);
  return report.invoices.find(
    (invoice) =>
      invoice.provider === provider &&
      invoice.currency.toUpperCase() === "USD" &&
      invoice.items.length > 0 &&
      invoice.items.every(
        (item) =>
          item.charge_type !== "fixed" &&
          item.service_start &&
          item.service_end &&
          Date.parse(item.service_start) === bounds.start &&
          Date.parse(item.service_end) === bounds.end,
      ) &&
      Math.abs(
        invoice.total -
          invoice.items.reduce((sum, item) => sum + item.amount, 0),
      ) < 0.000001,
  );
}
type ProviderMonth = {
  provider: "neon" | "cloudflare";
  lines: CloudCostLine[];
  fixed: NonNullable<CloudCostReport["fixed_costs"]>;
  invoice: CloudCostInvoice | undefined;
  amount: number | null;
  snapshot:
    | NonNullable<CloudCostReport["charge_snapshots"]>[number]
    | undefined;
  groups: ReturnType<typeof invoiceGroups>;
  unknown: number;
  status: "settled" | "unsettled";
};
export function serviceMonthProvider(
  report: CloudCostReport,
  provider: "neon" | "cloudflare",
): ProviderMonth {
  const bounds = monthBounds(report.month);
  const lines = report.lines.filter(
    (line) =>
      line.provider === provider &&
      Date.parse(line.start) >= bounds.start &&
      Date.parse(line.end) <= bounds.end,
  );
  const invoice = settledInvoice(report, provider);
  const snapshot = report.charge_snapshots?.find(
    (row) =>
      row.provider === provider &&
      row.month === report.month &&
      row.currency === "USD",
  );
  const known = lines.filter(
    (line) => line.amount !== null && line.currency.toUpperCase() === "USD",
  );
  const fixed = (report.fixed_costs ?? []).filter(
    (line) => line.provider === provider,
  );
  return {
    provider,
    lines,
    fixed,
    invoice,
    snapshot,
    amount: invoice
      ? invoice.total
      : snapshot
        ? snapshot.amount
        : known.length
          ? pricedTotal(known)
          : null,
    groups: invoice
      ? invoiceGroups([invoice])
      : snapshot
        ? invoiceGroups([
            {
              id: "snapshot",
              provider,
              issued_at: snapshot.checked_at,
              total: snapshot.amount,
              currency: snapshot.currency,
              source: snapshot.source,
              items: snapshot.items,
            },
          ])
        : categoryTotals(known),
    unknown: lines.length - known.length,
    status: invoice ? "settled" : "unsettled",
  };
}
export function usageStatus(
  line: CloudCostLine,
  report: CloudCostReport,
): "settled" | "unsettled" | "unverified" | "quantity_only" {
  const billed = report.invoices.some(
    (invoice) =>
      invoice.provider === line.provider &&
      invoice.items.some(
        (item) =>
          item.charge_type !== "fixed" &&
          item.charge_type !== "tax" &&
          item.charge_type !== "credit" &&
          item.service_start &&
          item.service_end &&
          Date.parse(line.start) >= Date.parse(item.service_start) &&
          Date.parse(line.end) <= Date.parse(item.service_end),
      ),
  );
  if (billed) return "settled";
  return line.provider === "neon" && line.amount === null
    ? "quantity_only"
    : line.amount === null
      ? "unverified"
      : "unsettled";
}
